import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';

interface AvatarPreviewProps {
  src: string;
  alt: string;
  thumbnailSize?: number;
  previewSize?: number;
  borderRadius?: string;
  style?: React.CSSProperties;
}

const AvatarPreview: React.FC<AvatarPreviewProps> = ({
  src,
  alt,
  thumbnailSize = 40,
  previewSize = 300,
  borderRadius = '8px',
  style,
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const thumbRef = useRef<HTMLImageElement>(null);

  const updatePosition = useCallback(() => {
    if (!thumbRef.current) return;
    const rect = thumbRef.current.getBoundingClientRect();
    const gap = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const effectiveSize = Math.min(previewSize, vw - 20, vh - 20);
    if (effectiveSize < 150) {
      setShowPreview(false);
      return;
    }

    let left: number;
    if (rect.right + gap + effectiveSize + 10 <= vw) {
      left = rect.right + gap;
    } else {
      left = rect.left - gap - effectiveSize;
    }

    let top = rect.top + rect.height / 2 - effectiveSize / 2;
    top = Math.max(10, Math.min(top, vh - effectiveSize - 10));

    setPosition({ top, left });
  }, [previewSize]);

  useEffect(() => {
    if (!showPreview) return;

    const hideOnScroll = () => setShowPreview(false);
    window.addEventListener('scroll', hideOnScroll, true);
    return () => window.removeEventListener('scroll', hideOnScroll, true);
  }, [showPreview]);

  const handleMouseEnter = () => {
    if (imageError || !src) return;
    updatePosition();
    setShowPreview(true);
  };

  const handleMouseLeave = () => {
    setShowPreview(false);
  };

  if (!src) return null;

  const effectiveSize = Math.min(previewSize, window.innerWidth - 20, window.innerHeight - 20);

  return (
    <>
      <img
        ref={thumbRef}
        src={src}
        alt={alt}
        onError={() => setImageError(true)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          width: `${thumbnailSize}px`,
          height: `${thumbnailSize}px`,
          objectFit: 'cover',
          borderRadius: '4px',
          cursor: 'pointer',
          ...style,
        }}
      />
      {showPreview && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            zIndex: 2000,
            pointerEvents: 'none',
            background: 'white',
            border: '1px solid #ddd',
            borderRadius,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            padding: '4px',
          }}
        >
          <img
            src={src}
            alt={alt}
            style={{
              width: `${effectiveSize}px`,
              height: `${effectiveSize}px`,
              objectFit: 'contain',
              borderRadius,
              display: 'block',
            }}
          />
        </div>,
        document.body
      )}
    </>
  );
};

export default AvatarPreview;
